import { CSSProperties, useMemo } from 'react';

import cx from 'classnames';
import type { GroupBase, OnChangeValue, OptionProps } from 'react-select-5';
import { components } from 'react-select-5';

import Checkbox from 'components/shared/FormAdapters/FormDefault/CheckBox';
import Icon from 'components/shared/Icon';
import Visibility from 'components/shared/Icon/icons/Visibility';

import s from './index.module.css';

type Props<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = OptionProps<Option, IsMulti, Group> & {
  checked?: boolean;
  hasPreview?: boolean;
  onPreview?: () => void;
  style?: CSSProperties;
};

export function CheckboxOption<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: Props<Option, IsMulti, Group>) {
  const {
    children,
    innerProps,
    innerRef,
    data,
    style,
    getClassNames,
    getStyles,
    getValue,
    hasPreview,
    onPreview,
    selectProps,
    checked: pChecked,
  } = props;

  const dataWithMetaProps = data as Option & { __selectAll__?: boolean; __isNew__?: boolean };

  const isSelectAllOption = props.isMulti && dataWithMetaProps['__selectAll__'];

  const getOptionLabel = selectProps.getOptionLabel || ((option) => (option as unknown as { label: string }).label);
  const getOptionValue = selectProps.getOptionValue || ((option) => (option as unknown as { value: string }).value);

  const isAllSelected = useMemo(() => {
    if (!props.isMulti) return false;

    return props.getValue().length === props.options.length - 1;
  }, [props.isMulti && props.getValue().length]);

  const checked = useMemo(() => {
    if (pChecked !== undefined) return pChecked;
    if (dataWithMetaProps.__selectAll__) return isAllSelected;

    return Boolean(getValue().find((option) => getOptionValue(option) === getOptionValue(data)));
  }, [
    dataWithMetaProps.__selectAll__,
    getOptionValue(data),
    getValue,
    isAllSelected,
    props.getValue().length,
    pChecked,
  ]);

  const handleSelectAll = () => {
    if (isAllSelected) {
      props.setValue([] as unknown as OnChangeValue<Option, IsMulti>, 'deselect-option');
      return;
    }
    props.setValue(
      (props.options as (Option & { __selectAll__?: boolean })[]).filter(
        (o) => !o['__selectAll__'],
      ) as unknown as OnChangeValue<Option, IsMulti>,
      'select-option',
    );
  };

  if (dataWithMetaProps['__isNew__']) {
    return (
      <div
        {...innerProps}
        title={getOptionLabel(data)}
        className={cx(s.option, getClassNames('option', props))}
        style={{ ...(getStyles('option', props) as CSSProperties), ...style }}
      >
        {children}
      </div>
    );
  }

  return (
    <components.Option<Option, IsMulti, Group>
      {...props}
      innerProps={{
        ...innerProps,
        title: getOptionLabel(data),
        style: { ...(getStyles('option', props) as CSSProperties), ...style },
        onClick: (e) => {
          if (isSelectAllOption) {
            handleSelectAll();
          } else {
            innerProps.onClick && innerProps.onClick(e);
          }

          e.preventDefault();
        },
      }}
      innerRef={innerRef}
      className={cx(s.option, { [s.selectAll]: isSelectAllOption }, getClassNames('option', props))}
    >
      <Checkbox type="primary" className={s.checkbox} checked={checked} readOnly />

      <div className={s.label}>
        <span className={s.labelTitle}>{children}</span>
      </div>

      {hasPreview && (
        <Icon
          component={Visibility}
          width="15px"
          height="14px"
          className={s.previewIcon}
          onClick={(event) => {
            event.stopPropagation();
            onPreview?.();
          }}
        />
      )}
    </components.Option>
  );
}
